/**
 * Lógica pura dos cards da grade: ordenação, forma do card, a decisão entre o
 * valor e o `—`, e a comparação que alimenta o `memo`.
 *
 * Fica fora do `.tsx` de propósito — o Node 24 remove tipos de `.ts`, mas não
 * compila JSX, e é só assim que isto roda em `node --test`.
 *
 * E por isso o arquivo **não tem import de runtime**: o `tsconfig` resolve como
 * bundler (`'./formato'`, sem extensão) e o Node exige a extensão explícita
 * (`'./formato.ts'`, que o `tsc` recusa com TS5097). As duas regras não cabem no
 * mesmo especificador. `import type` não entra nessa conta: é apagado antes de
 * rodar. Quem formata é o `.tsx`, chamando `formato.ts`.
 *
 * Os nomes de classe CSS vêm do protótipo (`card`, `vel-card`) e ficam como
 * estão: mudá-los aqui desalinharia a folha de estilo já conferida.
 */

import type { Divisao } from './api'

/**
 * O que a tela mostra quando ninguém mediu. **Nunca `R$ 0`** — zero é um
 * número, a ausência de número não é zero, e no telão da War Room um
 * "R$ 0,0 mi" ao lado de uma meta de R$ 3 mi é uma pergunta na sala.
 */
export const SEM_MEDICAO = '—'

/**
 * `vendido: null` é "ninguém mediu", diferente de zero medido.
 *
 * É um guard de tipo para o `.tsx` passar o valor a `milhoes()` sem `!`, com o
 * `noUncheckedIndexedAccess` e o `strict` ligados.
 */
export function foiMedido(valor: number | null | undefined): valor is number {
  return typeof valor === 'number' && Number.isFinite(valor)
}

/**
 * O valor que posiciona a agulha. Sem medição a agulha fica no início da
 * escala, mas o número exibido continua `—`: são coisas diferentes e a tela não
 * pode fundi-las (meta cadastrada sem coleta não é meta não atingida).
 */
export function vendidoParaAgulha(valor: number | null | undefined): number {
  return foiMedido(valor) ? valor : 0
}

/**
 * A forma do card vem do CADASTRO, não do código: PL, Malhas/Tecidos e Vila
 * Flor não têm subitem; AKZ/LND/FICO têm três; LEZ/H33 têm outros três. Divisão
 * nova com 1 ou 4 subitens aparece sem tocar em código.
 */
export function temSubitens(divisao: Pick<Divisao, 'subitens'>): boolean {
  return Array.isArray(divisao.subitens) && divisao.subitens.length > 0
}

/** Sem subitem o card é `.simples`: a sobra vira velocímetro em vez de buraco. */
export function classeDoCard(divisao: Pick<Divisao, 'subitens'>): string {
  return temSubitens(divisao) ? 'card' : 'card simples'
}

/** No card simples o velocímetro é o grande, que ocupa a sobra de altura. */
export function classeDoVelocimetro(divisao: Pick<Divisao, 'subitens'>): string {
  return temSubitens(divisao) ? 'vel-card' : 'vel-simples'
}

/* Divisão cadastrada sem `ordem` vai para o fim em vez de para a frente: `0`
   implícito a jogaria na primeira posição do telão. */
const ORDEM_AUSENTE = Number.MAX_SAFE_INTEGER

/**
 * Ordena pela `ordem` do cadastro, com o nome como desempate — sem isso duas
 * divisões com a mesma ordem trocam de lugar entre repinturas, e no telão isso
 * se lê como se a tela tivesse pulado.
 *
 * Devolve CÓPIA: o array vem do estado do React e `sort` ordena no lugar.
 */
export function ordenarDivisoes(divisoes: readonly Divisao[]): Divisao[] {
  return [...divisoes].sort((a, b) => {
    const ordemA = Number.isFinite(a.ordem) ? a.ordem : ORDEM_AUSENTE
    const ordemB = Number.isFinite(b.ordem) ? b.ordem : ORDEM_AUSENTE
    if (ordemA !== ordemB) return ordemA - ordemB
    return a.nome.localeCompare(b.nome, 'pt-BR')
  })
}

/**
 * Comparação campo a campo para o `memo` do card.
 *
 * A grade repinta a cada pedido que entra, por 4 dias. O delta do SSE cria
 * objeto novo para a divisão que mudou e **mantém o mesmo** para as outras sete
 * — mas o `vendidoTotal` e o relógio do cabeçalho fazem o Dashboard repintar de
 * qualquer jeito. Esta função é o que impede os 8 velocímetros de reconciliar
 * quando só um valor mudou.
 */
export function mesmaDivisao(a: Divisao, b: Divisao): boolean {
  if (a === b) return true
  if (
    a.id !== b.id ||
    a.nome !== b.nome ||
    a.sigla !== b.sigla ||
    a.logo !== b.logo ||
    a.ordem !== b.ordem ||
    a.meta !== b.meta ||
    a.vendido !== b.vendido
  ) {
    return false
  }

  const subitensA = a.subitens ?? []
  const subitensB = b.subitens ?? []
  if (subitensA.length !== subitensB.length) return false

  for (let i = 0; i < subitensA.length; i += 1) {
    const subA = subitensA[i]
    const subB = subitensB[i]
    if (!subA || !subB) return false
    if (
      subA.codigo !== subB.codigo ||
      subA.rotulo !== subB.rotulo ||
      subA.meta !== subB.meta ||
      subA.vendido !== subB.vendido ||
      subA.obs !== subB.obs
    ) {
      return false
    }
  }
  return true
}
